/**
 * Cover Slide
 * The site's first page, as the deck's first slide: its water, its mark
 * and its name, with what the product is and how a wish moves through it.
 *
 * The water is the site's own (`WaterBackground`, beneath the router): the
 * slide says `backdrop: 'water'` and the viewer clears its ground for it.
 * The name and the line under it use the homepage hero's own type.
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';
import { HOUSE_H, HOUSE_PATH, HOUSE_W } from '@components/wee/house-mark';
import { HeroReveal } from '@components/landing/hero-intro';
import { EASE_OUT_EXPO, FREEZE_FOR_CAPTURE } from '@components/motion/scroll-motion';
import { SlideBase } from '@components/press/pitch-deck/slide-primitives';
import { useDeckOpening } from '@components/press/pitch-deck/hooks/use-deck-opening';
import { HeroHeadline, HeroSubheadline } from '@pages/landing-page.styles';
import { colors } from '@config/colors';
import { spacing } from '@config/spacing';
import { typography } from '@config/typography';
import { media } from '@config/breakpoints';

/**
 * The cover's arrival, in seconds from the deck opening. It is the
 * homepage's (`hero-intro.tsx`) without the white-out or the mark's exit:
 * the water is already lit behind the password gate, and here the mark
 * stays as the logo. Settled by 2.25s, inside the ~3s the PDF/PPTX export
 * waits before capturing the slide (`scripts/generate-deck-pdf.cjs`, which
 * still lives in home-ceo-mono-repo-deprecated and runs against this site).
 */
const COVER_CUE = {
  mark: 0.1,
  shine: 0.75,
  title: 0.85,
  subtitle: 1.1,
  info: 1.35,
} as const;

/** The mark's box, close around its ink: the shine is clipped to the ink,
    so it needs no room of its own to cross. */
const BOX_W = 104;
const BOX_H = 86;
const INK_X = (BOX_W - HOUSE_W) / 2;
const INK_Y = (BOX_H - HOUSE_H) / 2;

/* The title's size, which the mark is drawn in proportion to: sized
   against the smaller of the screen's width and height, so the slide
   scales like a projected one (96px at 1920×1080). */
const TITLE_SIZE = 'clamp(2.25rem, min(5vw, 8.9vh), 8rem)';
/** The mark's ink is this many title-ems wide — about a third of the name. */
const MARK_INK_EM = 2.2;

const ID = 'deck-cover-mark';
const url = (suffix: string) => `url(#${ID}-${suffix})`;

/** What the cover says under the name: the Pre-Seed deck's lines unless
 *  given others (DTemplate's vision line and its analogy), and how wide
 *  the mark's ink is, in title-ems. */
type CoverProps = { subtitle?: React.ReactNode; info?: React.ReactNode; markEm?: number };

export const CoverSlide: React.FC<CoverProps> = ({ subtitle, info, markEm = MARK_INK_EM }) => {
  const opening = useDeckOpening();
  const settled = (useReducedMotion() ?? false) || FREEZE_FOR_CAPTURE;
  // Fixed at mount: only the deck's opening arrives. Coming back to the
  // cover, the slide transition is its entrance.
  const [arriving] = useState(opening && !settled);

  const line = (at: number, children: React.ReactNode) =>
    arriving ? <HeroReveal at={at}>{children}</HeroReveal> : children;

  return (
    <SlideBase>
      <Copy>
        {/* Out of nothing and into focus, as the homepage's mark
            resolves — then it stays. */}
        <MarkBox
          initial={arriving ? { opacity: 0, scale: 0.88, filter: 'blur(16px)' } : false}
          animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
          transition={{ duration: 0.8, ease: EASE_OUT_EXPO, delay: COVER_CUE.mark }}
          style={markEm === MARK_INK_EM ? undefined : { width: markWidth(markEm) }}
        >
          <CoverMark shine={arriving} />
        </MarkBox>
        {line(COVER_CUE.title, <Title>The Home CEO</Title>)}
        {line(
          COVER_CUE.subtitle,
          <Subtitle>{subtitle ?? 'Specialized AI assistant for Moms Managing Households'}</Subtitle>,
        )}
        {line(
          COVER_CUE.info,
          <Info>
            {info ?? (
              <>
                <span>Moms interact with AI through a Wish, Explore, Enact process.</span>{' '}
                <span>
                  For every wish a mom submits, backend AI agents add Domain Expert Modeling,{' '}
                  <Whole>Pre-Fetch</Whole> Data, and Improve UI/Assistant Capabilities.
                </span>
              </>
            )}
          </Info>,
        )}
      </Copy>
    </SlideBase>
  );
};

/** The homepage's arriving mark, in deep seafoam, with its shine when it arrives. */
const CoverMark: React.FC<{ shine: boolean }> = ({ shine }) => (
  <svg viewBox={`0 0 ${BOX_W} ${BOX_H}`} aria-hidden="true" focusable="false">
    <defs>
      <linearGradient id={`${ID}-fill`} x1="0" y1="0" x2="0.35" y2="1">
        <stop offset="0" stopColor={colors.brand.exxxtraDark} />
        <stop offset="0.5" stopColor={colors.brand.exxtraDark} />
        <stop offset="1" stopColor={colors.brand.exxxtraDark} />
      </linearGradient>
      {/* Straight across the band: its tilt comes from the group that
          carries it (see hero-intro.tsx). */}
      <linearGradient id={`${ID}-shine`} x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor={colors.wee.markShimmerClear} />
        <stop offset="0.35" stopColor={colors.wee.markShimmer} />
        <stop offset="0.5" stopColor={colors.wee.lanternCore} />
        <stop offset="0.65" stopColor={colors.wee.markShimmer} />
        <stop offset="1" stopColor={colors.wee.markShimmerClear} />
      </linearGradient>
      <clipPath id={`${ID}-clip`}>
        <path d={HOUSE_PATH} transform={`translate(${INK_X} ${INK_Y})`} />
      </clipPath>
    </defs>
    <path d={HOUSE_PATH} transform={`translate(${INK_X} ${INK_Y})`} fill={url('fill')} />
    {shine && (
      <g clipPath={url('clip')}>
        <g transform={`rotate(-18 ${BOX_W / 2} ${BOX_H / 2})`}>
          {/* `attrX`, not `x`: on an SVG element motion's `x` is a
              transform, which would fight the rotation above. */}
          <motion.rect
            y={-BOX_H}
            width={HOUSE_W * 0.55}
            height={BOX_H * 3}
            fill={url('shine')}
            initial={{ attrX: -HOUSE_W, opacity: 0 }}
            animate={{ attrX: [-HOUSE_W, BOX_W], opacity: [0, 1, 1, 0] }}
            transition={{
              duration: 0.6,
              times: [0, 0.15, 0.7, 1],
              delay: COVER_CUE.shine,
              ease: 'easeInOut',
            }}
          />
        </g>
      </g>
    )}
  </svg>
);

/* On a phone the words would run under the viewer's ‹ › buttons (44px,
   16px in from each side), so the column keeps clear of them there. */
const Copy = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0 ${spacing['4xl']};

  ${media.md} {
    padding: 0;
  }
`;

/** The mark's box width for an ink this many title-ems wide. */
const markWidth = (inkEm: number) => `calc(${TITLE_SIZE} * ${(inkEm * BOX_W) / HOUSE_W})`;

/* Everything on the cover scales with the screen, as a projected slide
   does, sized against the smaller of its width and height so a short
   window cannot push the words off it. The mark and the gap under it are
   measured in the title's size, so the two keep their proportion at every
   width. At 1920×1080: title 96px, mark 211px wide, subtitle 48px, info
   line 26px. */
const MarkBox = styled(motion.div)`
  width: ${markWidth(MARK_INK_EM)};
  aspect-ratio: ${BOX_W} / ${BOX_H};
  margin-bottom: calc(${TITLE_SIZE} * 0.3);

  svg {
    display: block;
    width: 100%;
    height: 100%;
    overflow: visible;
  }
`;

const Title = styled(HeroHeadline)`
  font-size: ${TITLE_SIZE};
  margin: 0 0 ${spacing.lg};
`;

const Subtitle = styled(HeroSubheadline)`
  font-size: clamp(1.375rem, min(2.5vw, 4.45vh), 4rem);
  margin: 0 0 ${spacing['2xl']};
  max-width: 30em;
  text-wrap: balance;
`;

/* An aside, not a third headline: smaller and lighter than the lines
   above it, on a soft grey panel the water shows through. Each sentence
   starts its own line — the process, then what the agents add to every
   wish. */
const Info = styled.p`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: clamp(1rem, min(1.35vw, 2.4vh), 2.25rem);
  font-weight: 300;
  line-height: 1.6;
  color: ${colors.highlight.primary};
  max-width: 38em;
  margin: 0;
  padding: 0.75em 1.5em;
  background: ${colors.neutral.light}73;
  border: 1px solid ${colors.white}66;
  border-radius: 0.75em;

  > span {
    display: block;
    text-wrap: balance;
  }
`;

/** A term that reads wrong split at its hyphen ("Pre-" / "Fetch"). */
const Whole = styled.span`
  white-space: nowrap;
`;
